import { useCallback, useEffect, useState } from "react";
import {
  useUIStore,
  DRAWER_WIDTH_MIN,
  DRAWER_WIDTH_MAX,
} from "../../store/uiStore";
import { useCellDetail } from "../../hooks/useCellDetail";
import { DrawerHeader } from "./DrawerHeader";
import { VlmSection } from "./VlmSection";
import { EquitySection } from "./EquitySection";
import { EquityImpactPanel } from "./EquityImpactPanel";
import { RiskPanel } from "./RiskPanel";
import { ChatPanel } from "./ChatPanel";
import { EvidenceCache } from "./EvidenceCache";
import { LoadingSpinner } from "../shared/LoadingSpinner";
import { ErrorBanner } from "../shared/ErrorBanner";
import { drawerPalette, type DrawerPalette } from "../../lib/drawerTheme";
import { useIsMobile } from "../../lib/useIsMobile";

export function CellDrawer() {
  const { activeTick, selectedCellId, drawerOpen } = useUIStore();
  const theme = useUIStore((s) => s.theme);
  const palette = drawerPalette(theme);
  const drawerWidth = useUIStore((s) => s.drawerWidth);
  const [isResizing, setIsResizing] = useState(false);
  const isMobile = useIsMobile();
  const { cell, isLoading, error } = useCellDetail(selectedCellId, activeTick);

  // Mobile: full-screen overlay (slides in over the map). The map stays
  // mounted underneath so going back to it is instant.
  // Desktop: side panel that pushes the map.
  const innerWidth = isMobile ? "100vw" : drawerWidth;
  const containerStyle: React.CSSProperties = isMobile
    ? {
        position: "fixed",
        top: 52,                              // sit below AppHeader
        right: 0,
        // Use 0 (not safe-area inset) — children handle their own padding
        // and we want the drawer's background to fill the home-indicator
        // area instead of leaving a strip of map peeking through.
        bottom: 0,
        width: drawerOpen ? "100vw" : 0,
        overflow: "hidden",
        transition: "width 0.25s ease",
        display: "flex",
        flexDirection: "column",
        background: palette.bg,
        borderLeft: `1px solid ${palette.border}`,
        zIndex: 1500,
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
      }
    : {
        position: "relative",
        width: drawerOpen ? drawerWidth : 0,
        minWidth: drawerOpen ? drawerWidth : 0,
        overflow: "hidden",
        // Suspend the open/close animation while the user is dragging the
        // resize handle — otherwise every mousemove tick would re-trigger
        // the 250ms transition and the drag feels laggy.
        transition: isResizing ? "none" : "width 0.25s ease, min-width 0.25s ease",
        display: "flex",
        flexDirection: "column",
        background: palette.bg,
        borderLeft: `1px solid ${palette.border}`,
        zIndex: 500,
      };

  return (
    <div style={containerStyle}>
      {drawerOpen && (
        <>
          {!isMobile && (
            <DrawerResizer
              palette={palette}
              isResizing={isResizing}
              setIsResizing={setIsResizing}
            />
          )}
          <div
            style={{
              width: innerWidth,
              display: "flex",
              flexDirection: "column",
              height: "100%",
              overflowY: "auto",
            }}
          >
            {isLoading && (
              <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <LoadingSpinner size={36} />
              </div>
            )}

            {error && (
              <div style={{ padding: 16 }}>
                <ErrorBanner message="Could not load cell details." />
              </div>
            )}

            {/* Even when DB has no row for this cell_id, we still allow chat — useful
                when the user clicks a tract whose cell_predictions row has not been
                materialized in the demo DB. */}
            {selectedCellId && !cell && !isLoading && (
              <DrawerEmptyChat
                cellId={selectedCellId}
                tickId={activeTick}
              />
            )}

            {cell && (
              <>
                <DrawerHeader
                  cellId={cell.cell_id}
                  pHat={cell.p_hat}
                  sviQuintile={cell.equity?.svi_quintile ?? null}
                  priorityRank={cell.brief?.priority_rank ?? null}
                />

                <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 16 }}>
                  <RiskPanel
                    pHat={cell.p_hat}
                    pTildeDisplay={cell.p_tilde_display ?? null}
                    groupThreshold={cell.group_threshold ?? null}
                    alertTriggered={cell.alert_triggered ?? null}
                  />
                  {cell.equity && <EquityImpactPanel equity={cell.equity} />}
                  <EvidenceCache
                    hasCachedVlm={cell.vlm != null}
                    hasCachedEquity={cell.equity != null}
                  />

                  <Divider />

                  <ChatPanel
                    cellId={cell.cell_id}
                    tickId={cell.tick_id}
                    cellContext={{
                      cell_id: cell.cell_id,
                      lat: cell.latitude,
                      lon: cell.longitude,
                      p_hat: cell.p_hat,
                      p_tilde: cell.p_tilde,
                      p_tilde_display: cell.p_tilde_display,
                      group_threshold: cell.group_threshold,
                      alert_triggered: cell.alert_triggered,
                      svi_quintile: cell.equity?.svi_quintile,
                      geoid: cell.equity?.geoid,
                    }}
                  />

                  {(cell.vlm || cell.equity) && <Divider />}

                  {cell.vlm && <VlmSection vlm={cell.vlm} />}

                  {cell.vlm && cell.equity && <Divider />}

                  {cell.equity && <EquitySection equity={cell.equity} />}
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// Vertical drag handle on the left edge of the drawer. Pulls the boundary
// rightward (narrower drawer) or leftward (wider drawer); persists the
// chosen width via uiStore.setDrawerWidth → localStorage.
function DrawerResizer({
  palette,
  isResizing,
  setIsResizing,
}: {
  palette: DrawerPalette;
  isResizing: boolean;
  setIsResizing: (v: boolean) => void;
}) {
  const setDrawerWidth = useUIStore((s) => s.setDrawerWidth);
  const [hover, setHover] = useState(false);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsResizing(true);
      const onMove = (ev: PointerEvent) => {
        const next = window.innerWidth - ev.clientX;
        const clamped = Math.min(DRAWER_WIDTH_MAX, Math.max(DRAWER_WIDTH_MIN, next));
        setDrawerWidth(clamped);
      };
      const onUp = () => {
        setIsResizing(false);
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      // Lock the rest of the page from text-selecting while dragging.
      document.body.style.userSelect = "none";
      document.body.style.cursor = "ew-resize";
    },
    [setDrawerWidth, setIsResizing],
  );

  // Cleanup if component unmounts mid-drag.
  useEffect(() => {
    return () => {
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
  }, []);

  const active = isResizing || hover;
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize cell drawer"
      title="Drag to resize"
      onPointerDown={onPointerDown}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "absolute",
        top: 0,
        bottom: 0,
        left: 0,
        width: 6,
        cursor: "ew-resize",
        zIndex: 510,
        background: active ? palette.accentLink : "transparent",
        // A faint vertical rule the user can spot when scanning the seam.
        borderLeft: `1px solid ${active ? "transparent" : palette.border}`,
        transition: isResizing ? "none" : "background 120ms ease",
      }}
    />
  );
}

function Divider() {
  const theme = useUIStore((s) => s.theme);
  const palette = drawerPalette(theme);
  return <div style={{ height: 1, background: palette.divider, margin: "0 -16px" }} />;
}

function DrawerEmptyChat({ cellId, tickId }: { cellId: string; tickId: string | null }) {
  const theme = useUIStore((s) => s.theme);
  const palette = drawerPalette(theme);
  return (
    <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 16 }}>
      <h2 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: palette.bodyText }}>
        Cell / tract {cellId}
      </h2>
      <p style={{ fontSize: 12, color: palette.mutedText, margin: 0 }}>
        No pre-computed risk in the database for this tick. Ask the chat to
        materialize an analysis on demand.
      </p>
      {tickId && (
        <ChatPanel cellId={cellId} tickId={tickId} cellContext={{ cell_id: cellId }} />
      )}
    </div>
  );
}
